import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/20/solid';
import { computeLoanTermYears, calcGuaranteeFee, calcAnnualPmt, calcDSCR, calcProjectCost } from '../../utils/loanMath';

const PrequalFinancialMetricsDisplay = ({ ocrData = {}, loanData = {} }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const years = Object.keys(ocrData).sort((a, b) => {
    return new Date(a) - new Date(b);
  });

  const calculateEBITDAOwnersComp = (yearData) => {
    const baseIncome = Number(yearData.ordinary_business_income || 0);
    const originalOfficerCompensation = Number(yearData.compensation_of_officers || 0);
    const interest = Number(yearData.interest || 0);
    const depreciation = Number(yearData.depreciation || 0);
    const amortization = Number(yearData.amortization || 0);
    
    return baseIncome + originalOfficerCompensation + interest + depreciation + amortization;
  };

  const calculateAddBacks = (otherDeductions) => {
    // If otherDeductions is an array, sum up all the amounts
    if (Array.isArray(otherDeductions)) {
      return otherDeductions.reduce((total, deduction) => {
        return total + Number(deduction.amount || 0);
      }, 0);
    }
    
    // For backward compatibility, if otherDeductions is an object
    if (typeof otherDeductions === 'object' && otherDeductions !== null) {
      return Object.entries(otherDeductions).reduce((total, [category, amount]) => {
        return total + Number(amount || 0);
      }, 0);
    }
    
    return 0;
  };

  const calculateSDE = (yearData) => {
    if (!yearData) return 0;
    const ebitdaOwnersComp = calculateEBITDAOwnersComp(yearData);
    const addbacksTotal = calculateAddBacks(yearData.other_deductions || []);
    
    return ebitdaOwnersComp + addbacksTotal;
  };

  const calculateNOI = (yearData) => {
    if (!yearData) return 0;
    const sde = calculateSDE(yearData);
    const newOfficerCompensation = Number(yearData.new_officer_compensation || 75000);
    return sde - newOfficerCompensation;
  };

  // Interest rate range: Prime (6.75%) + 2% to Prime + 2.75%
  const LOW_RATE = 0.095;   // 9.5%
  const HIGH_RATE = 0.1025; // 10.25%

  const getAnnualDebtServiceRange = () => {
    const purchasePrice = Number(loanData.prequalifiedAmount || loanData.purchasePrice || 0);
    const realEstateValue = Number(loanData.realEstateValue || 0);
    const equityPct = Number(loanData.prequalificationEquityPercent || 10);
    const project = calcProjectCost(purchasePrice, { equityPercent: equityPct });

    if (project.loanAmount <= 0) {
      return { low: 0, high: 0 };
    }

    const termYears = computeLoanTermYears({ purchasePrice, realEstateValue });

    return {
      low: calcAnnualPmt({ principal: project.loanAmount, rate: LOW_RATE, termYears }),
      high: calcAnnualPmt({ principal: project.loanAmount, rate: HIGH_RATE, termYears })
    };
  };

  // Use high rate for conservative DSCR
  const getAnnualDebtService = () => {
    return getAnnualDebtServiceRange().high;
  };

  const getDSCR = (noi) => {
    const annualDebtService = getAnnualDebtService();
    return calcDSCR({ noi, annualDebtService });
  };

  const getFinancialData = (key) => {
    return years.map(year => {
      const yearData = ocrData[year] || {};
      let value;
      switch (key) {
        case 'dscr':
          value = getDSCR(calculateNOI(yearData));
          break;
        case 'noi':
          value = calculateNOI(yearData);
          break;
        case 'ordinaryBusinessIncome':
          value = yearData.ordinary_business_income || 0;
          break;
        default:
          value = 0;
      }
      return { name: year.replace('Business Tax Return - ', ''), value };
    });
  };

  const financialStats = [
    { 
      name: 'DSCR', 
      stat: formatNumber(getDSCR(calculateNOI(ocrData[years[years.length-1]] || {}))),
      previousStat: formatNumber(getDSCR(calculateNOI(ocrData[years[years.length-2]] || {}))),
      change: ((getDSCR(calculateNOI(ocrData[years[years.length-1]] || {})) / getDSCR(calculateNOI(ocrData[years[years.length-2]] || {})) - 1) * 100).toFixed(2) + '%',
      changeType: getDSCR(calculateNOI(ocrData[years[years.length-1]] || {})) > getDSCR(calculateNOI(ocrData[years[years.length-2]] || {})) ? 'increase' : 'decrease',
      data: getFinancialData('dscr'),
      color: '#008081'
    },
    { 
      name: 'Net Operating Income', 
      stat: formatCurrency(calculateNOI(ocrData[years[years.length-1]] || {})),
      previousStat: formatCurrency(calculateNOI(ocrData[years[years.length-2]] || {})),
      change: ((calculateNOI(ocrData[years[years.length-1]] || {}) / calculateNOI(ocrData[years[years.length-2]] || {}) - 1) * 100).toFixed(2) + '%',
      changeType: calculateNOI(ocrData[years[years.length-1]] || {}) > calculateNOI(ocrData[years[years.length-2]] || {}) ? 'increase' : 'decrease',
      data: getFinancialData('noi'),
      color: '#6593F5'
    },
    { 
      name: 'Ordinary Business Income', 
      stat: formatCurrency(Number(ocrData[years[years.length-1]]?.ordinary_business_income || 0)),
      previousStat: formatCurrency(Number(ocrData[years[years.length-2]]?.ordinary_business_income || 0)),
      change: ((Number(ocrData[years[years.length-1]]?.ordinary_business_income || 0) / Number(ocrData[years[years.length-2]]?.ordinary_business_income || 1) - 1) * 100).toFixed(2) + '%',
      changeType: Number(ocrData[years[years.length-1]]?.ordinary_business_income || 0) > Number(ocrData[years[years.length-2]]?.ordinary_business_income || 0) ? 'increase' : 'decrease',
      data: getFinancialData('ordinaryBusinessIncome'),
      color: '#3FE0D0'
    }
  ];

  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {financialStats.map((item) => (
          <FinancialStatCard key={item.name} item={item} />
        ))}
      </div>

      <table className="min-w-full bg-white border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-4 py-2 border-b text-left">Metric</th>
            {years.map(year => (
              <th key={year} className="px-4 py-2 border-b text-right">{year.replace('Business Tax Return - ', '')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[
            { key: 'gross_receipts_or_sales', label: 'Gross Receipts or Sales' },
            { key: 'cost_of_goods_sold', label: 'Cost of Goods Sold', isNegative: true },
            { key: 'ordinary_business_income', label: 'Ordinary Business Income', isBold: true },
            { key: 'compensation_of_officers', label: '+ Original Officer Compensation' },
            { key: 'interest', label: '+ Interest' },
            { key: 'depreciation', label: '+ Depreciation' },
            { key: 'amortization', label: '+ Amortization' },
            { key: 'ebitdaOwnersComp', label: 'EBITDA + Owners Comp', calculate: calculateEBITDAOwnersComp, isBold: true, hasBorder: true },
            { key: 'ebitdaOwnersCompPercentage', label: 'EBITDA + Owners Comp %', calculate: (yearData) => (calculateEBITDAOwnersComp(yearData) / Number(yearData.gross_receipts_or_sales || 1)) * 100, isPercentage: true },
          ].map(({ key, label, calculate, isBold, hasBorder, isNegative, isPercentage }) => (
            <tr key={key} className={`hover:bg-gray-50 ${hasBorder ? 'border-t-2 border-gray-300' : ''}`}>
              <td className={`px-4 py-2 border-b ${isBold ? 'font-bold' : 'font-medium'} ${isNegative ? 'text-red-600' : ''}`}>{label}</td>
              {years.map(year => (
                <td key={year} className={`px-4 py-2 border-b text-right ${isBold ? 'font-bold' : ''} ${isNegative ? 'text-red-600' : ''}`}>
                  {calculate 
                    ? (isPercentage
                        ? `${formatNumber(calculate(ocrData[year]))}%`
                        : formatCurrency(calculate(ocrData[year])))
                    : (isPercentage
                        ? `${formatNumber((Number(ocrData[year]?.[key]) / Number(ocrData[year]?.gross_receipts_or_sales || 1)) * 100)}%`
                        : formatCurrency(Number(ocrData[year]?.[key] || 0)))}
                </td>
              ))}
            </tr>
          ))}
          <tr className="bg-gray-100">
            <td colSpan={years.length + 1} className="px-4 py-2 border-b font-bold">Add Backs</td>
          </tr>
          {years.map(year => {
            const yearData = ocrData[year] || {};
            const addBacks = Array.isArray(yearData.other_deductions) ? yearData.other_deductions : [];
            
            return addBacks.length > 0 ? (
              // If we have add-backs, display each one
              addBacks.map((addBack, index) => (
                <tr key={`${year}-${index}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 border-b pl-8 font-medium text-green-600">+ {addBack.category}</td>
                  {years.map(y => (
                    <td key={y} className="px-4 py-2 border-b text-right text-green-600">
                      {y === year ? formatCurrency(Number(addBack.amount || 0)) : ''}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              // If no add-backs, display a single empty row
              <tr key={`${year}-empty`} className="hover:bg-gray-50">
                <td className="px-4 py-2 border-b pl-8 font-medium text-gray-400">No add-backs</td>
                {years.map(y => (
                  <td key={y} className="px-4 py-2 border-b text-right"></td>
                ))}
              </tr>
            );
          })}
          {[
            { key: 'sde', label: 'Seller\'s Discretionary Earnings (SDE)', calculate: calculateSDE, isBold: true, hasBorder: true },
            { key: 'newOfficerCompensation', label: '- New Officer Compensation', calculate: (yearData) => -(Number(yearData.new_officer_compensation || 75000)), isNegative: true },
            { key: 'noi', label: 'Net Operating Income (NOI)', calculate: calculateNOI, isBold: true, hasBorder: true },
            { key: 'annualDebtService', label: 'Annual Debt Service', calculate: () => getAnnualDebtServiceRange(), isBold: false, isRange: true },
          ].map(({ key, label, calculate, isBold, hasBorder, isNegative, isRatio, isRange }) => (
            <tr key={key} className={`hover:bg-gray-50 ${hasBorder ? 'border-t-2 border-gray-300' : ''}`}>
              <td className={`px-4 py-2 border-b ${isBold ? 'font-bold' : 'font-medium'} ${isNegative ? 'text-red-600' : ''}`}>{label}</td>
              {years.map(year => {
                const val = calculate(ocrData[year]);
                return (
                  <td key={year} className={`px-4 py-2 border-b text-right ${isBold ? 'font-bold' : ''} ${isNegative ? 'text-red-600' : ''}`}>
                    {isRange
                      ? `${formatCurrency(val.low)} - ${formatCurrency(val.high)}`
                      : isRatio
                        ? formatNumber(val)
                        : formatCurrency(val)}
                  </td>
                );
              })}
            </tr>
          ))}
          
          {/* Debt Service Calculation Details */}
          {(() => {
            const purchasePrice = Number(loanData.prequalifiedAmount || loanData.purchasePrice || 0);
            const realEstateValue = Number(loanData.realEstateValue || 0);
            const equityPct = Number(loanData.prequalificationEquityPercent || 10);
            const project = calcProjectCost(purchasePrice, { equityPercent: equityPct });

            let termYearsForDisplay = computeLoanTermYears({ purchasePrice, realEstateValue });
            if (isNaN(termYearsForDisplay)) {
              termYearsForDisplay = 10;
            }

            const debtRange = getAnnualDebtServiceRange();
            const monthlyLow = debtRange.low / 12;
            const monthlyHigh = debtRange.high / 12;

            return [
              {
                key: 'totalProjectCost',
                label: `   Total Project Cost`,
                value: project.totalProjectCost,
                isBold: true
              },
              {
                key: 'equity',
                label: `   Equity Injection (${equityPct}%)`,
                value: project.equity,
                isBold: true
              },
              {
                key: 'loanAmount',
                label: `   SBA Loan (${100 - equityPct}%)`,
                value: project.loanAmount,
                isBold: true
              },
              {
                key: 'guaranteeFee',
                label: '      incl. SBA Guarantee Fee',
                value: project.guaranteeFee
              },
              {
                key: 'packagingFee',
                label: '      incl. Packaging Fee',
                value: project.packagingFee
              },
              {
                key: 'closingCosts',
                label: '      incl. Closing Costs',
                value: project.closingCosts
              },
              {
                key: 'interestRate',
                label: '   Interest Rate',
                value: 'Prime + 2% - Prime + 2.75% (8.75% - 9.50%)',
                isText: true
              },
              {
                key: 'loanTermDisplay',
                label: '   Loan Term',
                value: `${termYearsForDisplay} Years`,
                isText: true
              },
              {
                key: 'loanTermsNote',
                label: '',
                value: '(Backed by lender. Subject to change per borrower strength)',
                isNote: true
              },
              {
                key: 'monthlyPayment',
                label: '   Monthly Payment',
                value: `${formatCurrency(monthlyLow)} - ${formatCurrency(monthlyHigh)}`,
                isText: true,
                isBold: true
              },
              {
                key: 'annualDebtService',
                label: '   Annual Debt Service',
                value: `${formatCurrency(debtRange.low)} - ${formatCurrency(debtRange.high)}`,
                isText: true,
                isBold: true
              }
            ].map(({ key, label, value, isBold, isText, isNote }) => (
              <tr key={key} className={`hover:bg-gray-50 text-gray-600 ${isNote ? 'text-xs italic' : ''}`}>
                <td className={`px-4 py-2 border-b pl-8 ${isBold ? 'font-medium' : ''}`}>{label}</td>
                {years.map((year, idx) => (
                  <td key={`${year}-${idx}`} className={`px-4 py-2 border-b text-right ${isBold ? 'font-medium' : ''} ${isNote ? 'text-xs italic text-gray-400 text-left' : ''}`}>
                    {idx === 0 ?
                      (isText || isNote ? value : formatCurrency(value))
                      : ''}
                  </td>
                ))}
              </tr>
            ));
          })()}
          
          {/* DSCR line */}
          <tr className="hover:bg-gray-50 border-t- border-gray-300">
            <td className="px-4 py-2 border-b font-bold">Debt Service Coverage Ratio (DSCR)</td>
            {years.map(year => (
              <td key={year} className="px-4 py-2 border-b text-right font-bold">
                {formatNumber(getDSCR(calculateNOI(ocrData[year])))}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const FinancialStatCard = ({ item }) => (
  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
    <dt className="truncate text-sm font-medium text-gray-500">{item.name}</dt>
    <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{item.stat}</dd>
    <dd className="mt-2 flex items-baseline justify-between md:block lg:flex">
      <div className="flex items-baseline text-sm font-semibold text-green-600">
        {item.changeType === 'increase' ? (
          <ArrowUpIcon className="h-5 w-5 flex-shrink-0 self-center text-green-500" aria-hidden="true" />
        ) : (
          <ArrowDownIcon className="h-5 w-5 flex-shrink-0 self-center text-red-500" aria-hidden="true" />
        )}
        <span className="sr-only"> {item.changeType === 'increase' ? 'Increased' : 'Decreased'} by </span>
        {item.change}
      </div>
      <div className="inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0">
        from {item.previousStat}
      </div>
    </dd>
    <div className="mt-3">
      <ResponsiveContainer width="100%" height={100}>
        <BarChart data={item.data}>
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="value" fill={item.color} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export default PrequalFinancialMetricsDisplay;
